import { useContext, useMemo, useState } from "react";
import {
  HandlePaymentActivationMutationVariables,
  Payment,
  PaymentType,
  useCancelUserSubscriptionMutation,
} from "../../../../generated/graphql";
import { PaymentItem } from "../PaymentItem";
import { Box, Paper } from "@material-ui/core";
import { Subtitle, StyledSwitch } from "../../../shared/Style/Style";
import { DeleteIconButton } from "../../../shared/Buttons/DeleteIconButton";
import { PaymentsContainer } from "../../Style";
import {
  getActivePayments,
  getPaymentsHistory,
  isExternalPayment,
} from "../utils";
import { UserInfoContext } from "../../../Main";
import { RefundSubscriptionModal } from "../../../shared/Modal/RefundSubscriptionModal";

interface SchoolPaymentsProps {
  payments: Payment[];
  isPersonal: boolean;
  activatePayment(variables: HandlePaymentActivationMutationVariables): void;
  deletePayment(id: string): void;
}

export const SchoolPayments = ({
  payments = [],
  isPersonal,
  activatePayment,
  deletePayment,
}: SchoolPaymentsProps) => {
  const [refundSubscriptionModalShow, setRefundSubscriptionModalShow] =
    useState("");
  const [cancelSubscription] = useCancelUserSubscriptionMutation();

  const activePayments = useMemo(() => getActivePayments(payments), [payments]);
  const paymentsHistory = useMemo(
    () => getPaymentsHistory(payments),
    [payments]
  );
  const isUserCanManageSubscriptions = useContext(
    UserInfoContext
  )?.userPermissions?.includes("CAN_MANAGE_SUBSCRIPTIONS");

  const onPaymentActivation = (
    id: string,
    isActivated: boolean,
    refundAmount?: number
  ) => {
    activatePayment({
      id,
      isActivated: !isActivated,
      refundAmount: refundAmount || undefined,
    });
  };

  if (!activePayments?.length && !paymentsHistory.length) return null;

  const showActionButtons = (paymentType: PaymentType) =>
    !isExternalPayment(paymentType) && isUserCanManageSubscriptions;

  return (
    <Paper elevation={3}>
      <PaymentsContainer>
        {activePayments?.length > 0 ? (
          <>
            <Subtitle>Список оплат</Subtitle>
            {activePayments?.map(({ id, ...props }) => (
              <Box key={id} display="flex">
                <PaymentItem key={id} isPersonal={isPersonal} payment={props} />

                {showActionButtons(props?.paymentType) ? (
                  <Box paddingLeft="18px">
                    <Box display="flex" alignItems="center">
                      <p>
                        {!props?.isActivated
                          ? "Надати доступ"
                          : "Відкликати доступ"}
                      </p>
                      <StyledSwitch
                        onChange={() => {
                          if (!props?.isActivated) {
                            onPaymentActivation(id, false);
                            return;
                          }
                          setRefundSubscriptionModalShow(id);
                        }}
                        checked={props?.isActivated}
                      />
                    </Box>
                    {refundSubscriptionModalShow === id && (
                      <RefundSubscriptionModal
                        open
                        total={props?.total}
                        onClose={() => setRefundSubscriptionModalShow("")}
                        onConfirm={(refundAmount: number) => {
                          onPaymentActivation(id, true, refundAmount);
                          setRefundSubscriptionModalShow("");
                        }}
                      />
                    )}
                    <Box display="flex">
                      <p>Видалити платіж</p>
                      <DeleteIconButton
                        item="платіж"
                        fz={20}
                        deleteHandler={() => deletePayment(id)}
                        disabled={!props?.isManual}
                      />
                    </Box>
                  </Box>
                ) : null}

                {props.paymentType === PaymentType.Liqpay &&
                isUserCanManageSubscriptions &&
                props?.isActivated ? (
                  <Box display="flex" alignItems="center" paddingLeft="18px">
                    <p>Деактивувати підписку в LiqPay</p>
                    <DeleteIconButton
                      item="підписку"
                      fz={20}
                      deleteHandler={async () => {
                        await cancelSubscription({
                          variables: {
                            paymentId: id,
                          },
                        });

                        window.location.reload();
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            ))}
          </>
        ) : null}

        {paymentsHistory.length > 0 ? (
          <>
            <Subtitle>Історія оплат</Subtitle>
            {paymentsHistory?.map(({ id, ...paymentItemProps }) => (
              <PaymentItem
                key={id}
                payment={paymentItemProps}
                isPersonal={isPersonal}
                isHistory
              />
            ))}
          </>
        ) : null}
      </PaymentsContainer>
    </Paper>
  );
};
